import { Analyse, OHNE_TAETIGKEIT } from "../state/schema";
import { berechneHaushalt, summeSparbeitraege, summeVersicherungsbeitraege } from "./calc";
import { ageFromISO, formatEUR, isValidEmail, isValidPhone, isValidPLZ, parseAmount } from "./format";

/* Validadores por sección (§42/§43). Devuelven mensajes de error (vacío = ok).
   Se usan tanto en el control de cada paso como en el envío final. */

export function vZiele(a: Analyse): string[] {
  const e: string[] = [];
  if (a.ziele.wichtig.length === 0) e.push("Elige al menos un aspecto que te resulte importante.");
  if (a.ziele.ziele.length === 0) e.push("Elige al menos un objetivo.");
  return e;
}

export function vStammdaten(a: Analyse): string[] {
  const e: string[] = [];
  const s = a.stammdaten;
  const req: [string, string][] = [
    [s.vorname, "tu nombre"], [s.nachname, "tu apellido"], [s.geburtsdatum, "tu fecha de nacimiento"],
    [s.geburtsort, "tu lugar de nacimiento"], [s.staatsangehoerigkeit, "tu nacionalidad"],
    [s.strasse, "tu calle"], [s.hausnummer, "tu número de la casa"], [s.plz, "tu código postal"],
    [s.ort, "tu ciudad"], [s.email, "tu correo electrónico"], [s.mobil, "tu número de móvil"],
    [s.familienstand, "tu estado civil"],
  ];
  for (const [val, name] of req) if (!String(val).trim()) e.push(`Indica ${name}.`);
  if (s.email && !isValidEmail(s.email)) e.push("Indica un correo electrónico válido.");
  if (s.mobil && !isValidPhone(s.mobil)) e.push("Indica un número de móvil válido.");
  if (s.plz && !isValidPLZ(s.plz)) e.push("El código postal debe tener 5 dígitos.");
  if (s.geburtsdatum) {
    const age = ageFromISO(s.geburtsdatum);
    if (age === null) e.push("Indica una fecha de nacimiento válida.");
    else if (age < 0 || age > 120) e.push("Indica una fecha de nacimiento plausible.");
  }
  return e;
}

/* Pregunta sobre los hijos – se plantea y valida en el paso „Profesión y familia"
   (no en el paso „Datos personales"). */
export function vKinder(a: Analyse): string[] {
  const e: string[] = [];
  const s = a.stammdaten;
  if (!s.hatKinder) e.push("Indica si tienes hijos.");
  if (s.hatKinder === "ja") {
    if (!String(s.anzahlKinder).trim()) e.push("Indica cuántos hijos tienes.");
    if (!String(s.alterKinder).trim()) e.push("Indica la edad de tus hijos.");
  }
  return e;
}

/* Situación profesional: datos obligatorios, porque el estado, la actividad y la
   formación son la base del asesoramiento. La profesión solo se omite si la persona
   no ejerce ninguna actividad. */
export function vBeruf(a: Analyse): string[] {
  const e: string[] = [];
  const s = a.stammdaten;
  if (!String(s.berufsstatus ?? "").trim()) e.push("Indica tu situación laboral actual.");
  if (s.berufsstatus !== OHNE_TAETIGKEIT && !String(s.berufsbezeichnung ?? "").trim())
    e.push("Indica tu profesión exacta o tu actividad actual.");
  if (!String(s.abschluss ?? "").trim()) e.push("Indica tu título escolar o universitario.");
  return e;
}

/** El paso «Profesión y familia» valida a la vez los hijos y la situación profesional. */
export function vBerufFamilie(a: Analyse): string[] {
  return [...vKinder(a), ...vBeruf(a)];
}

export function vGesundheit(a: Analyse): string[] {
  const e: string[] = [];
  const g = a.gesundheit;
  if (!g.koerperlich) e.push("Responde la pregunta sobre tu salud física.");
  if (!g.psychisch) e.push("Responde la pregunta sobre tu salud mental.");
  if (!g.raucher) e.push("Indica si fumas.");
  if (!g.risikohobby) e.push("Responde la pregunta sobre pasatiempos de riesgo.");
  if (g.risikohobby === "ja" && !g.risikohobbyText.trim())
    e.push("Indica brevemente qué pasatiempos practicas.");
  return e;
}

export function vAltersvorsorge(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.altersvorsorge.vorhanden) e.push("Responde la pregunta sobre tu previsión de jubilación.");
  if (a.altersvorsorge.vorhanden === "ja") {
    if (a.altersvorsorge.vertraege.length === 0) e.push("Registra al menos un contrato.");
    a.altersvorsorge.vertraege.forEach((v, i) => {
      if (!v.vertragsart.trim()) e.push(`Contrato ${i + 1}: elige el tipo de contrato.`);
      if (!v.anbieterUnbekannt && !v.anbieter.trim()) e.push(`Contrato ${i + 1}: indica la compañía o marca «no lo sé».`);
      if (!v.beitragUnbekannt && !v.beitrag.trim()) e.push(`Contrato ${i + 1}: indica la cuota o marca «no lo sé».`);
    });
  }
  return e;
}

export function vArbeitskraft(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.arbeitskraft.vorhanden) e.push("Responde la pregunta sobre la protección de tu capacidad laboral.");
  if (a.arbeitskraft.vorhanden === "ja") {
    if (a.arbeitskraft.vertraege.length === 0) e.push("Registra al menos una cobertura.");
    a.arbeitskraft.vertraege.forEach((v, i) => {
      if (!v.vertragsart.trim()) e.push(`Cobertura ${i + 1}: elige el tipo de contrato.`);
    });
  }
  return e;
}

export function vSach(a: Analyse): string[] {
  const e: string[] = [];
  if (a.sachversicherungen.auswahl.length === 0)
    e.push('Elige qué seguros tienes (o «Ninguno»).');
  return e;
}

export function vVermoegen(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.vermoegen.vorhanden) e.push("Responde la pregunta sobre tu patrimonio.");
  if (a.vermoegen.vorhanden === "ja" && a.vermoegen.posten.length === 0)
    e.push("Elige al menos una categoría de patrimonio.");
  return e;
}

export function vStrom(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.strom.eigenerVertrag) e.push("Responde la pregunta sobre la electricidad.");
  if (a.strom.eigenerVertrag === "ja") {
    if (!a.strom.anbieter.trim()) e.push("Indica tu compañía de electricidad.");
    if (!String(a.strom.abschlag).trim()) e.push("Indica el pago mensual a cuenta (electricidad).");
    const ok = a.dokumente.some((d) => d.kategorie === "Stromabrechnung" && d.status === "done");
    if (!ok) e.push("Sube tu última factura de electricidad.");
  }
  return e;
}

export function vGas(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.gas.eigenerVertrag) e.push("Responde la pregunta sobre el gas.");
  if (a.gas.eigenerVertrag === "ja") {
    if (!a.gas.anbieter.trim()) e.push("Indica tu compañía de gas.");
    if (!String(a.gas.abschlag).trim()) e.push("Indica el pago mensual a cuenta (gas).");
    const ok = a.dokumente.some((d) => d.kategorie === "Gasabrechnung" && d.status === "done");
    if (!ok) e.push("Sube tu última factura de gas.");
  }
  return e;
}

export function vKrankenversicherung(a: Analyse): string[] {
  const e: string[] = [];
  const k = a.krankenversicherung;
  if (!k.gesetzlichVersichert) e.push("Indica si tienes seguro médico público (gesetzlich).");
  if (k.gesetzlichVersichert === "ja") {
    if (!String(k.anbieter ?? "").trim()) e.push("Indica el nombre de tu seguro médico (Krankenkasse).");
    if (!String(k.halt ?? "").trim()) e.push("Indica qué te mantiene actualmente en tu seguro médico.");
  }
  return e;
}

export function vEinkommen(a: Analyse): string[] {
  const e: string[] = [];
  const k = a.einkommen;
  const req: [string, string][] = [
    [k.bruttoMonat, "tu ingreso bruto"], [k.nettoMonat, "tu ingreso neto"],
    [k.nebeneinkuenfte, "tus ingresos adicionales"], [k.kindergeld, "la prestación por hijos (Kindergeld)"],
    [k.weitereEinnahmen, "otros ingresos disponibles"],
  ];
  for (const [val, name] of req)
    if (String(val).trim() === "") e.push(`Indica ${name} (0 € si no aplica).`);
  if (k.gehaltszahlungen === "otra" && !String(k.gehaltszahlungenAndere).trim())
    e.push("Indica cuántos pagos de salario recibes al año.");
  // Comprobante de ingresos (§18)
  if (!a.einkommensnachweis.art) e.push("Elige qué comprobante de ingresos vas a subir.");
  if (a.einkommensnachweis.art === "keine" && !a.einkommensnachweis.begruendung.trim())
    e.push("Explica brevemente por qué ahora no dispones de una nómina.");
  if (a.einkommensnachweis.art === "jahresendabrechnung" || a.einkommensnachweis.art === "gehaltsabrechnung") {
    const ok = a.dokumente.some((d) => d.kategorie === "Einkommensnachweis" && d.status === "done");
    if (!ok) e.push("Sube al menos un comprobante de ingresos correctamente.");
  }
  return e;
}

export function vAusgaben(a: Analyse): string[] {
  const e: string[] = [];
  const g = a.ausgaben;
  const felder: [string, string][] = [
    [g.wohnen, "tus gastos de vivienda"], [g.lebenshaltung, "tus gastos de vida diaria"],
    [g.mobilitaet, "tu movilidad"], [g.kreditraten, "tus cuotas de crédito"],
    [g.unterhalt, "tu pensión alimenticia"], [g.versicherungen, "tus cuotas de seguros"],
    [g.abosVereine, "tus suscripciones y asociaciones"], [g.sonstige, "tus otros gastos"],
    [g.sparbeitraege, "tus aportaciones de ahorro actuales"],
  ];
  for (const [val, name] of felder)
    if (String(val).trim() === "") e.push(`Indica ${name} (0 € posible).`);

  // Aportaciones de ahorro tomadas automáticamente (de los bienes patrimoniales)
  const sumS = summeSparbeitraege(a);
  const spar = parseAmount(g.sparbeitraege);
  if (spar < sumS - 0.005)
    e.push(`Tus aportaciones de ahorro no pueden ser menores que los productos de ahorro que registraste arriba (${formatEUR(sumS)}).`);
  if (spar > sumS + 0.005 && String(g.sparZielText ?? "").trim() === "")
    e.push("Indica brevemente a dónde va tu aportación de ahorro adicional.");

  // Cuotas de seguros tomadas automáticamente (de jubilación + capacidad laboral + seguros)
  const sumV = summeVersicherungsbeitraege(a);
  const vers = parseAmount(g.versicherungen);
  if (vers < sumV - 0.005)
    e.push(`Tus cuotas de seguros no pueden ser menores que los contratos que registraste arriba (${formatEUR(sumV)}).`);
  if (vers > sumV + 0.005 && String(g.versZielText ?? "").trim() === "")
    e.push("Indica brevemente qué seguros adicionales cubre tu cuota más alta.");
  return e;
}

export function vZukunft(a: Analyse): string[] {
  const e: string[] = [];
  const z = a.zukunft;
  if (String(z.mindestens).trim() === "") e.push("Indica el importe mínimo mensual (0 € posible).");
  if (String(z.komfortabel).trim() === "") e.push("Indica el importe máximo (0 € posible).");
  if (String(z.einmalig).trim() === "") e.push("Indica el importe disponible de una sola vez (0 € posible).");
  return e;
}

export function vPraeferenzen(a: Analyse): string[] {
  const e: string[] = [];
  if (!a.verantwortung) e.push("Responde la pregunta sobre la responsabilidad.");
  if (!a.praeferenz) e.push("Indica qué te resultaría más difícil de aceptar.");
  return e;
}

export function vBerater(a: Analyse): string[] {
  return a.beraterId ? [] : ["Elige a tu asesor personal."];
}

export function vEinwilligungen(a: Analyse): string[] {
  const e: string[] = [];
  const c = a.einwilligungen;
  if (!c.angabenGeprueft) e.push("Confirma que has revisado tus datos.");
  if (!c.datenschutz) e.push("Confirma que has leído la política de privacidad.");
  if (!c.verarbeitung) e.push("Acepta el tratamiento de tus datos.");
  if (!c.uebermittlung) e.push("Acepta la transmisión de tus datos a Blacksmith Finance.");
  if (!c.weiterleitung) e.push("Acepta el envío a tu asesor.");
  if (!c.driveSpeicherung) e.push("Acepta el almacenamiento de los documentos en el Drive protegido.");
  if (!c.gesundheit) e.push("Acepta por separado el tratamiento de tus datos de salud.");
  return e;
}

/* ---- Avisos de plausibilidad amables (no bloqueantes, §43) ---- */
export function plausibilityWarnings(a: Analyse): string[] {
  const w: string[] = [];
  const brutto = parseAmount(a.einkommen.bruttoMonat);
  const netto = parseAmount(a.einkommen.nettoMonat);
  if (brutto > 0 && netto > brutto)
    w.push("Tu ingreso neto es mayor que tu ingreso bruto. Revisa brevemente los valores.");
  const h = berechneHaushalt(a);
  if (parseAmount(a.ausgaben.wohnen) === 0)
    w.push("No indicaste gastos de vivienda. Si es correcto, no hay problema.");
  if (h.einnahmenGesamt > 0 && h.laufendeAusgaben > h.einnahmenGesamt)
    w.push("Tus gastos superan tus ingresos. No pasa nada: lo revisamos juntos.");
  return w;
}

export function investitionsWarnung(a: Analyse): string | null {
  const h = berechneHaushalt(a);
  const komfort = parseAmount(a.zukunft.komfortabel);
  if (h.freiVerfuegbar > 0 && komfort > h.freiVerfuegbar * 1.2) {
    return "El importe que deseas está claramente por encima de lo que tienes disponible ahora mismo. No es un problema: tu asesor verá contigo las posibles opciones.";
  }
  return null;
}

/** Validación agregada para el envío final. */
export function validateAll(a: Analyse): string[] {
  return [
    ...vZiele(a), ...vStammdaten(a), ...vKinder(a), ...vBeruf(a), ...vGesundheit(a), ...vAltersvorsorge(a),
    ...vArbeitskraft(a), ...vSach(a), ...vVermoegen(a), ...vStrom(a), ...vGas(a),
    ...vKrankenversicherung(a), ...vEinkommen(a), ...vAusgaben(a), ...vZukunft(a), ...vPraeferenzen(a),
    ...vBerater(a), ...vEinwilligungen(a),
  ];
}
